import { useState } from "react";
import { type AgentFormData, AgentFormFields } from "@/components/agents/AgentFormFields";
import { AgentModalShell, type AgentModalTab } from "@/components/agents/AgentModalShell";
import { CapabilitiesSelection } from "@/components/agents/EditAgentModal";
import { useApp } from "@/context/AppContext";
import { parseRoutingKeywords } from "@/lib/routingKeywords";
import type {
  AgentCreate,
  AgentSkillAttach,
  AgentToolConfig,
  DataCollection,
  ModelTierDefault,
  SkillSummary,
  ToolSummary,
} from "@/types";
import type { AIModel } from "@/types/models";

// ============================================================================
// Create Agent Modal
// ============================================================================

export interface CreateAgentModalProps {
  onClose: () => void;
  onSave: (data: AgentCreate, skills: AgentSkillAttach[]) => Promise<void>;
  models: AIModel[];
  tierDefaults?: ModelTierDefault[];
  dataSources: DataCollection[];
  availableSkills: SkillSummary[];
  availableTools: ToolSummary[];
}

export function CreateAgentModal({
  onClose,
  onSave,
  models,
  tierDefaults = [],
  dataSources,
  availableSkills,
  availableTools,
}: Readonly<CreateAgentModalProps>) {
  const { teams } = useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<AgentModalTab>("general");
  const defaultTeamUuid = (teams.find((t) => t.isEveryoneTeam) || teams[0])?.uuid || "";
  const [formData, setFormData] = useState<AgentFormData>({
    name: "",
    description: "",
    color: "#EC622B",
    systemPrompt: "",
    modelId: tierDefaults.some((t) => t.tier === "thorough") ? "tier:thorough" : models[0]?.id || "",
    teamUuid: defaultTeamUuid,
    dataCollectionIds: [],
    routingKeywords: "",
  });
  const [selectedSkills, setSelectedSkills] = useState<AgentSkillAttach[]>([]);
  const [selectedToolConfigs, setSelectedToolConfigs] = useState<AgentToolConfig[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const routingKeywords = parseRoutingKeywords(formData.routingKeywords);
      await onSave(
        {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          systemPrompt: formData.systemPrompt,
          modelId: formData.modelId,
          teamUuid: formData.teamUuid,
          dataCollectionIds: formData.dataCollectionIds,
          toolConfigs: selectedToolConfigs,
          ...(routingKeywords.length > 0 ? { settings: { routing_keywords: routingKeywords } } : {}),
        },
        selectedSkills,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AgentModalShell
      title="Create New Agent"
      formId="create-agent-form"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      capabilitiesBadgeCount={selectedSkills.length + selectedToolConfigs.length}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={!!formData.name && !!formData.systemPrompt}
      submitLabel="Create Agent"
    >
      {activeTab === "general" && (
        <AgentFormFields
          idPrefix="create-agent"
          formData={formData}
          onChange={setFormData}
          models={models}
          tierDefaults={tierDefaults}
          dataSources={dataSources}
        />
      )}

      {activeTab === "capabilities" && (
        <CapabilitiesSelection
          availableSkills={availableSkills}
          availableTools={availableTools}
          selectedSkills={selectedSkills}
          selectedToolConfigs={selectedToolConfigs}
          onSkillsChange={setSelectedSkills}
          onToolsChange={setSelectedToolConfigs}
        />
      )}
    </AgentModalShell>
  );
}
